/**
 * [Modules] Reusable methods shared across the site. Typically, methods defined here _should_ be used across several pages.
 * As opposed to utilities, these methods are ran on document ready if its query is available.
 */
// Modules
import Toggle from '@borngroup/born-toggle';
import {breakpointRanges, initMethods} from './utilities';
import {callbackOnElements} from '@borngroup/born-utilities';

let modules = {
    toggles: {
        default: '[data-toggle]',
        init: function(query) {
            return new Toggle({
                triggers: query || this.default,
                breakpoints: breakpointRanges
            });
        }
    },
    carousels: {
        default: '[data-slick]',
        init: function(query) {
            return import(/* webpackChunkName: "module-carousels" */ '../modules/module.carousel').then(Carousel => {
                return new Carousel.default(query || this.default);
            });
        }
    },
    fixitTrigger: {
        default: '[data-fixit-trigger]',
        init: function(query, settings = {}) {
            return import(/* webpackChunkName: "module-fixit-trigger" */ '../modules/module.fixit-trigger').then(FixitTrigger => {
                return new FixitTrigger.default({elements: query || this.default, settings: settings});
            });
        }
    },
    modalTrigger: {
        default: '[data-modal-trigger]',
        init: function(query) {
            return import(/* webpackChunkName: "module-modal-trigger" */ '../modules/module.modal-trigger').then(ModalTrigger => {
                return new ModalTrigger.default({elements: query || this.default});
            });
        }
    },
    quickView: {
        default: '[data-quickview-trigger]',
        init: function() {
            return import(/* webpackChunkName: "module-quick-view" */ '../modules/module.quick-view').then(QuickView => {
                return initMethods(QuickView.init);
            });
        }
    },
    quickAdd: {
        default: '[data-quickadd-trigger]',
        init: function() {
            return import(/* webpackChunkName: "module-quick-add" */ '../modules/module.quick-add').then(QuickAdd => {
                return QuickAdd.init();
            });
        }
    },
    windowScrolls: {
        default: '[data-window-scroll]',
        init: function(query) {
            return import(/* webpackChunkName: "module-window-scroll" */ '../modules/module.window-scroll').then(WindowScroll => {
                return new WindowScroll.default(query || this.default);
            });
        }
    },
    wishlistTrigger: {
        default: '[data-wishlist-trigger]',
        init: function(query) {
            return import(/* webpackChunkName: "module-wishlist-trigger" */ '../modules/module.wishlist-trigger').then(WishlistTrigger => {
                return WishlistTrigger.init({elements: query || this.default});
            });
        }
    },
    saveForLaterTrigger: {
        default: '[data-saveforlater-trigger]',
        init: function(query) {
            if (window.SFRA.SitePreferences.enableSaveForLater) {
                return import(/* webpackChunkName: "module-saveforlater-trigger" */ '../modules/module.saveforlater-trigger').then(SaveForLaterTrigger => {
                    return SaveForLaterTrigger.init({elements: query || this.default});
                });
            }
        }
    },
    productTile: {
        default: '[data-product-tile]',
        init: function(query) {
            return import(/* webpackChunkName: "module-product-tile" */ '../modules/module.product-tile').then(ProductTile => {
                return new ProductTile.default({elements: query || this.default});
            });
        }
    },
    productTilePackshots: {
        default: '[data-product-packshots]',
        init: function() {
            return import('../product/productPackshots').then(productTilePackshots => {
                return new productTilePackshots.productPackshotInit();
            });
        }
    },
    productImageGalleryItem: {
        default: '[data-product-component="gallery-item"]',
        init: function() {
            return import('../product/productImageGalleryItem').then(productImageGalleryItem => {
                return new productImageGalleryItem.productImageGalleryItemInit();
            });
        }
    },
    productTileQuickShop: {
        default: '[data-product-quickshop]',
        init: function() {
            return import('../product/productQuickshop').then(productTileQuickShop => {
                return new productTileQuickShop.Init();
            });
        }
    },
    ajaxForm: {
        default: '[data-ajax-form]',
        init: function(query) {
            return import(/* webpackChunkName: "module-ajax-form" */ '../modules/module.ajax-form').then(AJAXForm => {
                return AJAXForm.init({elements: query || this.default});
            });
        }
    },
    recaptchaForm: {
        default: '[data-recaptcha-form]',
        init: function(query) {
            return import(/* webpackChunkName: "module-recaptcha-form" */ '../modules/module.recaptcha-form').then(RecaptchaForm => {
                return RecaptchaForm.init({elements: query || this.default});
            });
        }
    },
    videos: {
        default: '[data-video]',
        init: function(query) {
            return import(/* webpackChunkName: "module-video" */ '../modules/module.video').then(Video => {
                return new Video.default(query || this.default);
            });
        }
    },
    storeLocator: {
        default: '[data-locator]',
        init: function(query, options) {
            return import(/* webpackChunkName: "module-store-locator" */ '../modules/module.store-locator').then(StoreLocator => {
                return StoreLocator.init(query || this.default, options);
            });
        }
    },
    inputFormat: {
        default: '[data-input-format]',
        init: function(query) {
            return import(/* webpackChunkName: "module-input-format" */ '../modules/module.input-format').then(InputFormat => {
                return new InputFormat.default(query || this.default);
            });
        }
    },
    pageMotion: {
        default: '[data-page-motion]',
        init: function(query) {
            return import(/* webpackChunkName: "module-page-motion" */ '../modules/module.page-motion').then(PageMotion => {
                return new PageMotion.default({elements: query || this.default});
            });
        }
    },
    authTrigger: {
        default: '[data-auth-trigger]',
        init: function(query) {
            return import(/* webpackChunkName: "module-auth" */ '../modules/module.auth').then(Auth => {
                return Auth.init({elements: query || this.default});
            });
        }
    },
    addressVerification: {
        default: '[data-address-verification]',
        init: function(query) {
            if (window.SFRA.SitePreferences.addressVerificationEnabled) {
                return import(/* webpackChunkName: "module-address-verification" */ '../modules/module.address-verification').then(AddressVerification => {
                    return AddressVerification.init({elements: query || this.default});
                });
            }
        }
    },
    addressSuggestion: {
        default: '[data-address-suggestion]',
        init: function(query) {
            return import(/* webpackChunkName: "module-address-suggestion" */ '../modules/module.address-suggestion').then(AddressSuggestion => {
                callbackOnElements(query || this.default, (el) => new AddressSuggestion.default(el));
            });
        }
    },
    addressAutocomplete: {
        default: '[data-address-autocomplete]',
        init: function(query) {
            if (window.SFRA.SitePreferences.addressAutocompleteEnabled) {
                return import(/* webpackChunkName: "module-address-autocomplete" */ '../modules/module.address-autocomplete').then(AddressAutofill => {
                    return AddressAutofill.init({elements: query || this.default});
                });
            }
        }
    },
    floatingLabels: {
        default: '[data-floating-label]',
        init: function(query) {
            return import(/* webpackChunkName: "module-floating-labels" */ '../modules/module.floating-labels').then(FloatingLabels => {
                return new FloatingLabels.default(query || this.default);
            });
        }
    },
    tracking: {
        default: '[data-tracking]',
        init: function(query, options) {
            return import(/* webpackChunkName: "module-tracking-layer" */ '../modules/module.tracking-layer').then(TrackingLayer => {
                if (options) {
                    let action = options.action || 'pushData';

                    return TrackingLayer.default[action](options.trackEvent, options.trackObject);
                } else {
                    return new TrackingLayer.default({elements: query || this.default});
                }
            });
        }
    },
    passwordShow: {
        default: '[data-password-show="trigger"]',
        init: function(query) {
            return import(/* webpackChunkName: "module-password-show" */ '../modules/module.password-show').then(PasswordShow => {
                return PasswordShow.init({elements: query || this.default});
            });
        }
    },
    intlPhone: {
        default: '[data-intl-phone]',
        init: function(query) {
            return import(/* webpackChunkName: "module-intl-phone" */ '../modules/module.intl-phone').then(IntlPhone => {
                return IntlPhone.init({elements: query || this.default});
            });
        }
    },
    giftMsgFont: {
        default: '[data-gift-msg-font]',
        init: function(query) {
            return import(/* webpackChunkName: "module-giftmsg-font" */ '../modules/module.giftmsg-font').then(GiftMsgFont => {
                return new GiftMsgFont.default(query || this.default);
            });
        }
    },
    customSelect: {
        default: '[data-custom-select]',
        init: function(query) {
            return import(/* webpackChunkName: "module-custom-select" */ '../modules/module.custom-select').then(CustomSelect => {
                return CustomSelect.init({elements: query || this.default});
            });
        }
    },
    countrySelector: {
        default: '[data-select-country]',
        init: function() {
            return import(/* webpackChunkName: "module-country-selector" */ '../modules/module.country-selector').then(CountrySelector => {
                return initMethods(CountrySelector.init);
            });
        }
    },
    formfieldBinding: {
        default: '[data-formfield-binding]',
        init: function(query) {
            return import(/* webpackChunkName: "module-formfield-binding" */ '../modules/module.formfield-binding').then(FormfieldBinding => {
                callbackOnElements(query || this.default, (el) => new FormfieldBinding.default(el));
            });
        }
    },
    inputAutoApply: {
        default: '[data-input-auto-apply]',
        init: function(query) {
            return import(/* webpackChunkName: "module.input-auto-apply" */ '../modules/module.input-auto-apply').then(InputAutoApply => {
                callbackOnElements(query || this.default, (el) => new InputAutoApply.default(el));
            });
        }
    },
    navTabs: {
        default: '[data-navTabs]',
        init: function() {
            return import('../components/navTabs').then(navTabs => {
                return new navTabs.navTabsInit();
            });
        }
    },
    personalisationModalHelper: {
        default: '[data-personalisation-modal-helper]',
        init: function() {
            return import(/* webpackChunkName: "module.personalisation" */'../modules/module.personalisation').then(personalisationModal => {
                return new personalisationModal.default();
            });
        }
    },
    cmsSearch: {
        default: '[data-cms-search]',
        init: function() {
            return import(/* webpackChunkName: "module.cmsSearch" */'../components/cmsSearch').then(cmsSearch => {
                return new cmsSearch.cmsSearchInit();
            });
        }
    },
    langEnablement: {
        default: '[data-turkish-enablement]',
        init: function() {
            return import(/* webpackChunkName: "module.langEnablement" */'../components/langEnablement').then(langEnablement => {
                return new langEnablement.langEnablementInit();
            });
        }
    }
};

export default modules;
